import {
  AsyncPaginate,
  LoadOptions,
  ReduceOptions
} from "react-select-async-paginate";
import {
  components,
  FormatOptionLabelMeta,
  GroupBase,
  MultiValueRemoveProps,
  OptionProps,
  SelectComponentsConfig,
  StylesConfig
} from "react-select";
import { InputSize } from "shared/components/forms/DateInput";
import { cx } from "shared/utils";
import { sizedSelectStyles, defaultSelectTheme } from "shared/components";
import { ChangeEvent, ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { XClose } from "untitledui-js-base";

interface Option {
  label?: string;
  value: string | GroupBase<string | number> | number | boolean;
  isDisabled?: boolean;
  tooltip?: string;
}
interface AsyncSelectProps {
  label?: string;
  id: string;
  error?: string | boolean;
  size?: InputSize;
  disabled?: boolean;
  searchable?: boolean;
  value?:
    | string
    | GroupBase<string | number>
    | number
    | boolean
    | unknown[]
    | Option;
  onChange?: (
    value: Option[] | (ChangeEvent<HTMLSelectElement> & { label?: string })
  ) => void;
  loadOptions: LoadOptions<unknown, GroupBase<unknown>, unknown>;
  options?: Option[];
  additional?: { page: number } & Record<string, unknown>;
  reduceOptions?: ReduceOptions<unknown, GroupBase<unknown>, unknown>;
  isMulti?: boolean;
  required?: boolean;
  hideOptional?: boolean;
  defaultValue?: Option;
  placeholder?: string;
  isClearable?: boolean;
  styles?: StylesConfig<unknown, boolean, GroupBase<unknown>>;
  formatOptionLabel?: (
    option: Option,
    meta: FormatOptionLabelMeta<Option>
  ) => ReactNode;
  components?: Partial<
    SelectComponentsConfig<unknown, boolean, GroupBase<unknown>>
  >;
  labelClassname?: string;
}

const CustomOption = (
  props: OptionProps<unknown, boolean, GroupBase<unknown>>
) => {
  const { data } = props;
  const { tooltip } = data as Option;

  if (tooltip)
    return (
      <Tooltip placement="left">
        <TooltipTrigger asChild>
          <span>
            <components.Option {...props} />
          </span>
        </TooltipTrigger>
        <TooltipContent
          className="max-w-[300px] rounded-lg border bg-alpha-800 p-4 text-sm text-white z-[13] shadow-md"
          style={{ zIndex: 50 }}
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );

  return <components.Option {...props} />;
};

const CustomMultiValueRemove = (props: MultiValueRemoveProps) => {
  return (
    <components.MultiValueRemove {...props}>
      <XClose className="size-3 stroke-[3px]" />
    </components.MultiValueRemove>
  );
};

export const AsyncSelect = ({
  label,
  id,
  error,
  size,
  loadOptions,
  labelClassname,
  disabled = false,
  searchable = false,
  onChange,
  value,
  options,
  additional,
  isMulti = false,
  required,
  hideOptional,
  defaultValue,
  placeholder,
  styles,
  formatOptionLabel,
  components: componentsProp,
  ...rest
}: AsyncSelectProps) => {
  return (
    <>
      {label ? (
        <label
          className={cx(
            "mb-1 mr-auto block text-sm font-medium text-content-700",
            labelClassname
          )}
          htmlFor={id}
        >
          {label}
          {!required && !hideOptional ? (
            <span className="text-content-100"> (optional)</span>
          ) : null}
        </label>
      ) : null}
      <AsyncPaginate
        id={id}
        defaultValue={defaultValue}
        defaultOptions={options}
        isSearchable={searchable}
        isDisabled={disabled}
        isMulti={isMulti}
        loadOptions={loadOptions}
        additional={additional}
        styles={{
          ...sizedSelectStyles(size),
          ...styles
        }}
        theme={defaultSelectTheme}
        placeholder={placeholder}
        // Also replacing it with
        // options?.find((option) => option.value === value) || value
        // Breaks the select
        value={
          options ? options.find((option) => option.value === value) : value
        }
        // @ts-expect-error -- react-select's component map types fight the
        // loosely-typed option used across our async selects
        formatOptionLabel={formatOptionLabel}
        components={{
          Option: CustomOption,
          MultiValueRemove: CustomMultiValueRemove,
          ...componentsProp
        }}
        onChange={(newValue) => {
          if (onChange)
            Array.isArray(newValue)
              ? onChange(newValue as Option[])
              : onChange({
                  // @ts-expect-error -- react-select types are bs (x2)
                  target: { value: newValue?.value },
                  id,
                  name: id,
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  label: (newValue as Option)?.label
                });
        }}
        className={cx(
          "mb-2 block cursor-pointer rounded-md text-base  placeholder:text-base placeholder:font-normal placeholder:text-content-100",
          error ? "border-danger-500" : "border-alpha-100"
        )}
        {...rest}
      />
      {error ? <p className="text-danger-500 text-xs">{error}</p> : null}
    </>
  );
};
